/**
 * VatSummaryForm Component
 *
 * Table-based editor for the VAT breakdown. Renders the editable rate table,
 * derived totals, warning banners, the "Add rate" toolbar action and the
 * row-remove confirm sub-Modal.
 *
 * State + actions are owned by `useVatRowEditor`; this component is purely
 * presentational. The host is expected to call the hook itself and forward
 * the returned editor here so that the modal footer can drive `handleSave`
 * via the same editor instance.
 *
 * Only rates present in the persisted summary are shown — empty rates are not
 * pre-filled. Missing rates are added via "Add rate" or "Fill from line items".
 *
 * Uses `useTranslation('modules.modals.editVatSummary')` for all labels.
 */
import * as React from 'react';
import {
  Decimal,
  ZERO,
  formatAmount,
  parseDecimal,
  roundToCurrencyPrecision
} from '@insio/decimal';
import type { CurrencyCode } from '@insio/shared-types';
import {
  InvoiceField,
  InvoiceSection,
  IssueCodes,
  VatSummaryField,
  matchIndexedChild
} from '@insio/validation';
import { Modal } from '../../../components/Modal';
import { Button } from '../../../components/Button';
import { DecimalInput, Input } from '../../../components/inputs';
import { Icon } from '../../../components/icons/Icon';
import { CurrencySelector } from '../../../components/selects/CurrencySelector';
import { AlertBanner } from '../../validation/AlertBanner';
import { FieldErrorList } from '../../validation/FieldErrorList';
import { ErrorEntry } from '../../../components/errors';
import { ExpectedAmountHint } from '../../../components/ExpectedAmountHint';
import { useTranslation, useNumberFormat } from '../../../providers/UIProvider';
import { useInvoiceCurrencyCode } from '../../../providers/InvoiceProvider';
import { cn, combineIssues, severityStyle, withRoundedExpected } from '../../../utils';
import type { ValidationIssue } from '../../../types';
import type { UseVatRowEditorResult } from './hooks';
import {
  computeVatRowExpectations,
  type InternalVatRow,
  type VatSummaryCurrentData
} from './utils';

type RowCellKey = 'vatRate' | 'netAmount' | 'vatAmount';

interface RowIssues {
  /** Issues targeting the row itself (path `vatSummary.N` or with an unknown child). */
  row: ValidationIssue[];
  byField: Partial<Record<RowCellKey, ValidationIssue[]>>;
}

export type {
  VatSummaryItem,
  VatSummaryFormData,
  VatSummaryCurrentData,
  InternalVatRow
} from './utils';

const HEIGHT_SPAN_CLASS = 'h-10 flex items-center justify-end';

const Th: React.FC<{
  align?: 'left' | 'right';
  children?: React.ReactNode;
  className?: string;
}> = ({ align = 'right', children, className }) => (
  <th
    className={cn(
      'bg-background-muted py-sm text-xs font-semibold uppercase text-foreground-muted whitespace-nowrap',
      align === 'left' ? 'text-left px-md' : 'text-right px-sm',
      className
    )}
  >
    {children}
  </th>
);

export interface VatSummaryFormProps {
  editor: UseVatRowEditorResult;
  /** Open flag of the host modal — guards the nested remove-confirm dialog
   * so it can never render orphaned when the parent is closed. */
  open: boolean;
  currentData: VatSummaryCurrentData;
  currencyCode?: string | null;
  /**
   * Validation issues. `vatSummary.<i>[.<field>]` issues are bucketed by the
   * persisted row index `i` and shown as per-row/per-cell affordances;
   * `vatSummary` (section-level) and empty-path issues render as banners
   * above the table.
   */
  errors?: ValidationIssue[];
}

export const VatSummaryForm: React.FC<VatSummaryFormProps> = ({
  editor,
  open,
  currentData,
  currencyCode,
  errors
}) => {
  const t = useTranslation('modules.modals.editVatSummary');
  const numberFormat = useNumberFormat();
  const invoiceCurrencyCode = useInvoiceCurrencyCode();
  // Editor owns the active currency; `currencyCode` prop is just the initial
  // fallback when `currentData` has none. Live display reflects editor state
  // so column headers re-render as the user picks a different currency.
  const currency = editor.currencyCode ?? currencyCode ?? 'CZK';
  const fmt = (v: Decimal) =>
    formatAmount(v, { currencyCode: currency as CurrencyCode, numberFormat });

  // The rate only means something once the recap is reported in a currency other
  // than the invoice's — a stored value does not keep it visible. When the
  // invoice currency is unknown we cannot prove the two match, so an entered
  // value stays editable.
  const showExchangeRate =
    invoiceCurrencyCode != null
      ? currency !== invoiceCurrencyCode
      : editor.exchangeRateDisplay.trim() !== '';

  // True once the user switches the recap currency away from the one the
  // line-derived expectations and backend issues were computed in (the
  // `currencyCode` prop). Those comparisons are meaningless across currencies,
  // so they are suppressed until the currency matches again.
  const baseCurrency = currencyCode ?? 'CZK';
  const currencyMismatch = currency !== baseCurrency;

  // The backend `errors` describe the last-saved recap (`currentData.vatSummary`).
  // Once the editor rows diverge from that snapshot — via "Fill from line items",
  // add/remove row or an edit — those issues are stale, so they are suppressed
  // until the next save revalidates. Derived (not a flag) so it self-heals when
  // the saved snapshot updates. 0 %-row VAT is normalised to 0 on load, so
  // normalise both sides to avoid a false "modified" on open.
  const validatedRows = currentData.vatSummary ?? [];
  const normVat = (rate: number, vat: Decimal) => (rate === 0 ? ZERO : vat);
  // A persisted row is matched by its index (a rate may repeat, e.g. reverse
  // charge); a row the user added has none, so it is matched by rate.
  const persistedIndexOf = (row: InternalVatRow): number =>
    row.persistedIndex ?? validatedRows.findIndex(x => x.vatRate.toNumber() === row.vatRate);
  const recapModified =
    editor.vatRows.length !== validatedRows.length ||
    editor.vatRows.some(r => {
      const v = validatedRows[persistedIndexOf(r)];
      return (
        !v ||
        !r.netAmount.eq(v.netAmount) ||
        !normVat(r.vatRate, r.vatAmount).eq(normVat(r.vatRate, v.vatAmount))
      );
    });

  // Bucket errors by the *persisted* row index, so reordering by rate in the
  // editor doesn't break the lookup and two rows with one rate stay apart.
  const { errorsByIndex, sectionLevelIssues } = React.useMemo(() => {
    const byIndex = new Map<number, RowIssues>();
    const section: ValidationIssue[] = [];
    // Backend issues were computed for the persisted currency; drop them while
    // the recap currency differs. The parent revalidates on the next save.
    if (!errors?.length || currencyMismatch)
      return { errorsByIndex: byIndex, sectionLevelIssues: section };
    const persisted = currentData.vatSummary ?? [];
    for (const rawIssue of errors) {
      // Round numeric expected values to the currency precision so the message
      // and the apply affordance both surface a clean amount.
      const issue = withRoundedExpected(rawIssue, currency as CurrencyCode);
      const path = issue.path ?? '';
      if (path === '' || path === InvoiceSection.vatSummary) {
        section.push(issue);
        continue;
      }
      const match = matchIndexedChild(path, InvoiceSection.vatSummary);
      if (!match || !persisted[match.index]) continue;
      let bucket = byIndex.get(match.index);
      if (!bucket) {
        bucket = { row: [], byField: {} };
        byIndex.set(match.index, bucket);
      }
      const field = match.child;
      if (
        field === VatSummaryField.vatRate ||
        field === VatSummaryField.netAmount ||
        field === VatSummaryField.vatAmount
      ) {
        (bucket.byField[field] ??= []).push(issue);
      } else {
        bucket.row.push(issue);
      }
    }
    return { errorsByIndex: byIndex, sectionLevelIssues: section };
  }, [errors, currentData.vatSummary, currency, currencyMismatch]);

  // Rate issues are about the field being empty, not about amounts in a given
  // currency, so they survive a recap-currency switch — unlike the row issues
  // above. But the "missing rate" issue is stale the moment the user types.
  const exchangeRateIssues = React.useMemo(() => {
    const all = (errors ?? []).filter(
      issue => (issue.path ?? '') === InvoiceField.vatSummaryExchangeRate
    );
    if (editor.exchangeRateDisplay.trim() === '') return all;
    return all.filter(issue => issue.code !== IssueCodes.vatSummaryExchangeRateMissing.code);
  }, [errors, editor.exchangeRateDisplay]);
  const exchangeRateSeverity = severityStyle(combineIssues(exchangeRateIssues)?.severity);

  // Locally suppress a row's stale validation issues once the user enters any
  // of its cells — the messages are stale until the parent revalidates, and
  // keeping them on screen while editing is noisy. Reset on every new
  // validation cycle (new `errors` reference). Mirrors the invoice-lines form.
  // Keyed like `errorsByIndex`, so clearing one row never clears its same-rate twin.
  const [clearedRows, setClearedRows] = React.useState<ReadonlySet<number>>(() => new Set());
  React.useEffect(() => {
    setClearedRows(prev => (prev.size > 0 ? new Set() : prev));
  }, [errors]);
  const markRowCleared = React.useCallback((persistedIndex: number) => {
    setClearedRows(prev => {
      if (prev.has(persistedIndex)) return prev;
      const next = new Set(prev);
      next.add(persistedIndex);
      return next;
    });
  }, []);

  // "Fill from line items" is always available; when applying it would
  // overwrite an existing non-empty row, confirm via a modal first.
  const [fillConfirmOpen, setFillConfirmOpen] = React.useState(false);
  const handleFillFromLines = () => {
    if (editor.fillFromLinesWouldOverwrite) setFillConfirmOpen(true);
    else void editor.fillFromLines();
  };

  // Suppress the stale section banner once the recap diverges from the saved
  // snapshot (e.g. after "Fill from line items" re-adds a missing rate).
  const showSectionIssues = !recapModified && sectionLevelIssues.length > 0;
  const hasWarnings = !!editor.generalError || showSectionIssues;

  return (
    <div className='bg-background-input rounded-lg border border-border-base overflow-hidden'>
      <div className='px-md py-sm border-b border-border-base flex flex-col gap-sm sm:flex-row sm:items-center'>
        <span className='order-1 text-xs font-semibold uppercase text-foreground-muted sm:shrink-0'>
          {t('currencyLabel')}
        </span>
        <div className='order-3 w-full sm:order-2 sm:ml-auto sm:w-48'>
          <CurrencySelector
            isSearchable
            value={editor.currencyCode ?? ''}
            disabled={editor.saving}
            onChange={code => editor.setCurrencyCode(code === '' ? null : code)}
            placeholder={t('currencyPlaceholder')}
            className='w-full normal-case'
          />
        </div>
        <div className='order-2 flex flex-col gap-sm sm:order-3 sm:flex-row sm:shrink-0'>
          {editor.recomputeEnabled && (
            <Button
              variant='default'
              outline='default'
              size='sm'
              onClick={handleFillFromLines}
              disabled={editor.saving || editor.fillingFromLines}
              aria-label={t('actions.fillFromLines')}
              className='bg-white border font-semibold'
            >
              <Icon name='arrows-rotate' className='w-3.5 h-3.5 mr-sm' />
              {t('actions.fillFromLines')}
            </Button>
          )}
          <Button
            variant='default'
            outline='default'
            size='sm'
            onClick={editor.addCustomRow}
            disabled={editor.saving}
            aria-label={t('actions.addRate')}
            className='bg-white border font-semibold'
          >
            <Icon name='plus' className='w-3.5 h-3.5 mr-sm' />
            {t('actions.addRate')}
          </Button>
        </div>
      </div>

      {showExchangeRate && (
        <div
          data-hover-field={InvoiceField.vatSummaryExchangeRate}
          className='px-md py-sm border-b border-border-base flex flex-col gap-sm sm:flex-row sm:items-start'
        >
          <label
            htmlFor='vat-summary-exchange-rate'
            className='text-xs font-semibold uppercase text-foreground-muted sm:shrink-0 sm:h-10 sm:flex sm:items-center'
          >
            {t('exchangeRateLabel')}
          </label>
          <div className='w-full sm:ml-auto sm:w-48'>
            <Input
              id='vat-summary-exchange-rate'
              type='text'
              value={editor.exchangeRateDisplay}
              onChange={e => editor.setRawExchangeRate(e.target.value)}
              onBlur={editor.commitExchangeRate}
              onFocus={e => e.target.select()}
              disabled={editor.saving}
              placeholder='—'
              className={cn(
                '[&_input]:text-right [&_input]:text-xs',
                exchangeRateSeverity && `border ${exchangeRateSeverity.borderColor}`
              )}
            />
            <FieldErrorList issues={exchangeRateIssues} align='right' />
            {invoiceCurrencyCode && (
              <p className='mt-2xs text-right text-xs text-foreground-muted'>
                {t('exchangeRateHint', { from: invoiceCurrencyCode, to: currency })}
              </p>
            )}
          </div>
        </div>
      )}

      {hasWarnings && (
        <div className='px-md py-sm space-y-sm border-b border-border-base'>
          {editor.generalError && <AlertBanner variant='error' title={editor.generalError} />}
          {showSectionIssues &&
            sectionLevelIssues.map((issue, i) => (
              <AlertBanner
                key={`section-issue-${i}`}
                variant={issue.severity === 'error' ? 'error' : 'warning'}
                title={<ErrorEntry issue={issue} showActual />}
              />
            ))}
        </div>
      )}

      <div className='max-h-[50vh] overflow-auto'>
        <table className='w-full border-collapse'>
          <thead className='sticky top-0 z-10'>
            <tr className='bg-background-muted border-b border-border-base'>
              <Th align='left'>{t('vatRateLabel')}</Th>
              <Th>{t('baseLabel', { currency })}</Th>
              <Th>{t('vatLabel', { currency })}</Th>
              <Th>{t('totalInclVatLabel', { currency })}</Th>
              <th className='bg-background-muted py-sm px-sm w-7' />
            </tr>
          </thead>
          <tbody>
            {editor.vatRows.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className='py-lg px-md text-xs text-foreground-muted italic text-center'
                >
                  {t('noVatRates')}
                </td>
              </tr>
            )}

            {editor.vatRows.map((row, idx) => {
              // Cross-expectation gating: while the user is typing in one
              // amount cell, suppress that cell's own hint and only surface
              // the opposite-side expected value. Rows the user hasn't
              // touched fall through to showing both hints when applicable.
              const lastEdited = editor.lastEditedByRow.get(idx);
              // Prefer the backend line-derived expected amounts for this rate,
              // but only for untouched rows in the summary currency: once the
              // user edits a cell the opposite-side hint must track their typed
              // value via the row's own net↔vat math, and a currency switch
              // invalidates the line-derived (original-currency) amounts.
              const expectedFromLines =
                lastEdited || currencyMismatch ? null : editor.getExpectedForRate(row.vatRate);
              const { expectedVat, expectedNet, hasVatMismatch, hasNetMismatch, hasMismatch } =
                computeVatRowExpectations(row, expectedFromLines);
              const persistedIndex = persistedIndexOf(row);
              const rowIssues =
                recapModified || clearedRows.has(persistedIndex)
                  ? undefined
                  : errorsByIndex.get(persistedIndex);
              const rowCombined = combineIssues(rowIssues?.row);
              const rowSeverity = severityStyle(rowCombined?.severity);
              const rateCombined = combineIssues(rowIssues?.byField.vatRate);
              const rateSeverity = severityStyle(rateCombined?.severity);
              const netCombined = combineIssues(rowIssues?.byField.netAmount);
              const netSeverity = severityStyle(netCombined?.severity);
              const vatCombined = combineIssues(rowIssues?.byField.vatAmount);
              const vatSeverity = severityStyle(vatCombined?.severity);
              const hasNetIssue = !!rowIssues?.byField.netAmount?.length;
              const hasVatIssue = !!rowIssues?.byField.vatAmount?.length;
              // Show the expected-amount hint when the typed value diverges, but
              // not while editing that cell, and not when the cell already has a
              // validation issue (the issue carries its own apply affordance).
              const showNetHint = hasNetMismatch && lastEdited !== 'netAmount' && !hasNetIssue;
              const showVatHint = hasVatMismatch && lastEdited !== 'vatAmount' && !hasVatIssue;
              // Fill an amount cell from a validation issue's `meta.expected`.
              const applyExpectedToCell =
                (field: 'netAmount' | 'vatAmount') => (expected: string) => {
                  const parsed = parseDecimal(expected);
                  if (parsed == null) return;
                  editor.setCellAmount(
                    idx,
                    field,
                    roundToCurrencyPrecision(parsed, currency as CurrencyCode | undefined)
                  );
                  // The applied value satisfies the issue; drop the row's now-stale
                  // issues until revalidation (the button suppresses focus-clear).
                  markRowCleared(persistedIndex);
                };
              return (
                <tr
                  key={`${row.vatRate}-${idx}`}
                  // `onFocus` bubbles (React focusin): clears the row's stale
                  // issues as soon as the user enters any cell, not while typing.
                  onFocus={() => markRowCleared(persistedIndex)}
                  className={cn(
                    'border-b border-border-base hover:bg-background-muted',
                    hasMismatch && 'bg-warn-bg',
                    rowSeverity?.bgColor
                  )}
                >
                  <td
                    className={cn(
                      'py-sm px-md align-top',
                      rowSeverity && `border-l-[3px] ${rowSeverity.borderColor}`,
                      rateSeverity?.bgColor
                    )}
                  >
                    {row.isUserAdded ? (
                      <Input
                        type='text'
                        value={editor.cellVal(idx, 'vatRate', row.vatRate)}
                        onChange={e => editor.onCellChange(idx, 'vatRate', e.target.value)}
                        onBlur={() => editor.onCellBlur(idx, 'vatRate')}
                        onFocus={e => e.target.select()}
                        disabled={editor.saving}
                        className={cn(
                          '[&_input]:text-center [&_input]:text-xs font-semibold w-12',
                          rateSeverity && `border ${rateSeverity.borderColor}`
                        )}
                        placeholder='0–100'
                      />
                    ) : (
                      <span
                        className={cn(
                          'text-xs font-semibold h-10 flex items-center',
                          rateSeverity?.iconColor ??
                            (hasMismatch ? 'text-warn-text' : 'text-foreground-base')
                        )}
                      >
                        {editor.rateLabel(row.vatRate)}
                      </span>
                    )}
                    <FieldErrorList
                      issues={[...(rowIssues?.row ?? []), ...(rowIssues?.byField.vatRate ?? [])]}
                    />
                  </td>
                  <td className={cn('py-sm px-sm align-top', netSeverity?.bgColor)}>
                    <DecimalInput
                      currencyCode={currency as CurrencyCode}
                      value={editor.cellVal(idx, 'netAmount', row.netAmount)}
                      onChange={value => editor.onCellChange(idx, 'netAmount', value)}
                      onBlur={() => editor.onCellBlur(idx, 'netAmount')}
                      onFocus={e => e.target.select()}
                      disabled={editor.saving}
                      className={cn(
                        '[&_input]:text-right [&_input]:text-xs',
                        hasNetMismatch && !netSeverity && 'border-warn-border',
                        netSeverity && `border ${netSeverity.borderColor}`
                      )}
                    />
                    <FieldErrorList
                      issues={rowIssues?.byField.netAmount}
                      align='right'
                      onApplyExpected={applyExpectedToCell('netAmount')}
                      applyExpectedLabel={t('warnings.applyExpected')}
                    />
                    <ExpectedAmountHint
                      show={showNetHint}
                      label={t('warnings.expectedNet')}
                      amount={`${fmt(expectedNet)} ${currency}`}
                      formula={
                        expectedFromLines
                          ? t('warnings.expectedFromLines')
                          : t('warnings.expectedNetFormula', {
                              vat: fmt(row.vatAmount),
                              rate: row.vatRate,
                              currency
                            })
                      }
                      onApply={() =>
                        editor.setCellAmount(
                          idx,
                          'netAmount',
                          roundToCurrencyPrecision(expectedNet, currency as CurrencyCode)
                        )
                      }
                      applyLabel={t('warnings.applyExpected')}
                    />
                  </td>
                  <td className={cn('py-sm px-sm align-top', vatSeverity?.bgColor)}>
                    <DecimalInput
                      currencyCode={currency as CurrencyCode}
                      value={editor.cellVal(idx, 'vatAmount', row.vatAmount)}
                      onChange={value => editor.onCellChange(idx, 'vatAmount', value)}
                      onBlur={() => editor.onCellBlur(idx, 'vatAmount')}
                      onFocus={e => e.target.select()}
                      disabled={editor.saving || row.vatRate === 0}
                      className={cn(
                        '[&_input]:text-right [&_input]:text-xs',
                        hasVatMismatch && !vatSeverity && 'border-warn-border',
                        vatSeverity && `border ${vatSeverity.borderColor}`
                      )}
                    />
                    <FieldErrorList
                      issues={rowIssues?.byField.vatAmount}
                      align='right'
                      onApplyExpected={applyExpectedToCell('vatAmount')}
                      applyExpectedLabel={t('warnings.applyExpected')}
                    />
                    <ExpectedAmountHint
                      show={showVatHint}
                      label={t('warnings.expectedVat')}
                      amount={`${fmt(expectedVat)} ${currency}`}
                      formula={
                        expectedFromLines
                          ? t('warnings.expectedFromLines')
                          : t('warnings.expectedVatFormula', {
                              base: fmt(row.netAmount),
                              rate: row.vatRate,
                              currency
                            })
                      }
                      onApply={() =>
                        editor.setCellAmount(
                          idx,
                          'vatAmount',
                          roundToCurrencyPrecision(expectedVat, currency as CurrencyCode)
                        )
                      }
                      applyLabel={t('warnings.applyExpected')}
                    />
                  </td>
                  <td className='py-sm px-sm text-right text-xs text-foreground-base align-top whitespace-nowrap'>
                    <span className={HEIGHT_SPAN_CLASS}>
                      {fmt(row.netAmount.plus(row.vatAmount))}
                    </span>
                  </td>
                  <td className='py-sm px-sm align-top text-center'>
                    <span className='h-10 inline-flex items-center justify-center'>
                      <button
                        type='button'
                        tabIndex={-1}
                        onClick={() => editor.setRemoveConfirmIdx(idx)}
                        disabled={editor.saving}
                        aria-label={t('actions.removeRate')}
                        className='p-xs rounded text-foreground-muted hover:text-error-text hover:bg-error-bg transition-colors disabled:opacity-40 disabled:pointer-events-none'
                      >
                        <Icon name='trash-can' className='w-3.5 h-3.5' />
                      </button>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {editor.vatRows.length > 0 && (
            <tfoot>
              <tr className='bg-background-muted border-t border-border-base'>
                <td className='py-sm px-md text-xs font-bold text-foreground-base uppercase'>
                  {t('vatLabels.total')}
                </td>
                <td className='py-sm px-sm text-right text-xs font-bold text-foreground-base whitespace-nowrap'>
                  {fmt(editor.calcNetTotal)}
                </td>
                <td className='py-sm px-sm text-right text-xs font-bold text-foreground-base whitespace-nowrap'>
                  {fmt(editor.calcVatTotal)}
                </td>
                <td className='py-sm px-sm text-right text-xs font-bold text-foreground-base whitespace-nowrap'>
                  {fmt(editor.calcGrandTotal)}
                </td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Remove-confirmation sub-modal — portaled to document.body; guarded by
       * parent `open` to prevent orphaned dialog when host modal closes. */}
      <Modal
        open={open && editor.removeConfirmIdx !== null}
        onClose={() => editor.setRemoveConfirmIdx(null)}
        className='max-w-xs'
      >
        <Modal.Header
          label={t('removeConfirm.title')}
          description={
            editor.removeConfirmIdx !== null
              ? editor.rateLabel(editor.vatRows[editor.removeConfirmIdx]?.vatRate ?? 0)
              : ''
          }
          onClose={() => editor.setRemoveConfirmIdx(null)}
          variant='white'
          icon={<Icon name='trash' className='w-6 h-6 text-error-text' />}
        />
        <Modal.Content className='h-auto! min-h-0'>
          <p className='text-sm text-foreground-base'>{t('removeConfirm.body')}</p>
        </Modal.Content>
        <Modal.Footer variant='white'>
          <Modal.Action>
            <Button variant='ghost' size='md' onClick={() => editor.setRemoveConfirmIdx(null)}>
              {t('actions.cancelRemove')}
            </Button>
            <Button
              variant='error'
              size='md'
              onClick={() => {
                if (editor.removeConfirmIdx !== null) editor.removeRow(editor.removeConfirmIdx);
              }}
            >
              {t('actions.confirmRemove')}
            </Button>
          </Modal.Action>
        </Modal.Footer>
      </Modal>

      {/* Fill-from-lines confirmation — only shown when applying would overwrite
       * an existing non-empty row. Guarded by parent `open`. */}
      <Modal
        open={open && fillConfirmOpen}
        onClose={() => setFillConfirmOpen(false)}
        className='max-w-xs'
      >
        <Modal.Header
          label={t('fillConfirm.title')}
          onClose={() => setFillConfirmOpen(false)}
          variant='white'
          icon={<Icon name='arrows-rotate' className='w-6 h-6 text-primary-base' />}
        />
        <Modal.Content className='h-auto! min-h-0'>
          <p className='text-sm text-foreground-base'>{t('fillConfirm.body')}</p>
        </Modal.Content>
        <Modal.Footer variant='white'>
          <Modal.Action>
            <Button variant='ghost' size='md' onClick={() => setFillConfirmOpen(false)}>
              {t('fillConfirm.cancel')}
            </Button>
            <Button
              variant='primary'
              size='md'
              onClick={() => {
                void editor.fillFromLines();
                setFillConfirmOpen(false);
              }}
            >
              {t('fillConfirm.confirm')}
            </Button>
          </Modal.Action>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

VatSummaryForm.displayName = 'VatSummaryForm';
